import { http } from './request'

/** 消息等级：紧急 / 普通 / 一般（与后台约定一致） */
export type NotificationLevel = 'urgent' | 'normal' | 'general'

export interface NotificationItem {
  id: string
  title: string
  content: string
  time: string
  level: NotificationLevel
}

export function notificationLevelLabel(level: NotificationLevel): string {
  const m: Record<NotificationLevel, string> = {
    urgent: '紧急',
    normal: '普通',
    general: '一般',
  }
  return m[level]
}

/** Ant Design Vue Tag 的 color 预设，保证三类辨识清晰 */
export function notificationLevelTagColor(level: NotificationLevel): string {
  const m: Record<NotificationLevel, string> = {
    urgent: 'error',
    normal: 'warning',
    general: 'processing',
  }
  return m[level]
}

type SysIndexMessageListItem = {
  id?: string
  category?: string
  subject?: string
  content?: string
  createTime?: string
}

function guessLevelFromCategory(category?: string): NotificationLevel {
  const c = (category ?? '').trim().toLowerCase()
  if (!c) return 'general'
  if (/(urgent|danger|error|fatal)/.test(c)) return 'urgent'
  if (/(warn|warning|normal)/.test(c)) return 'normal'
  return 'general'
}

export type FetchNotificationListParams = {
  /** 条数上限 */
  limit?: number
  /** false/不传：未读；true：已读 */
  read?: boolean
}

/** 消息提醒弹窗/侧栏预览条数；角标与 Tab 数字仍用收件箱总数 */
export const INBOX_PREVIEW_LIMIT = 100

/** 获取当前用户收件箱站内信列表（后端按发送范围规则判定可见性，再按已读筛选） */
export async function fetchNotificationList(
  params?: FetchNotificationListParams,
): Promise<NotificationItem[]> {
  const limit = params?.limit ?? INBOX_PREVIEW_LIMIT
  const read = params?.read
  const rows = await http.get<SysIndexMessageListItem[]>('/sys/index/message/inbox', {
    params: { limit, ...(read === undefined ? {} : { read }) },
  })
  const list = Array.isArray(rows) ? rows : []
  return list.map((r, idx) => {
    const id = r?.id != null ? String(r.id).trim() : `msg-${idx}`
    const title = (r?.subject ?? '').toString().trim()
    const content = (r?.content ?? '').toString().trim()
    const time = (r?.createTime ?? '').toString().trim()
    const level = guessLevelFromCategory(r?.category)
    return {
      id,
      title: title || '站内信',
      content,
      time: time || '',
      level,
    } satisfies NotificationItem
  })
}

/** 打开收件箱详情并标记已读 */
export async function fetchNotificationDetail(id: string): Promise<void> {
  const mid = id.trim()
  if (!mid) return
  await http.get<unknown>('/sys/index/message/detail', { params: { id: mid } })
}

/** 未读站内信数量（角标） */
export async function fetchMessageUnreadCount(): Promise<number> {
  const n = await http.get<number>('/sys/index/message/unreadCount')
  return typeof n === 'number' && !Number.isNaN(n) ? n : 0
}

export type MessageCounts = {
  unreadCount: number
  readCount: number
}

/** 未读/已读总数（与列表 limit 无关，供角标与 Tab 统一展示） */
export async function fetchMessageCounts(): Promise<MessageCounts> {
  const data = await http.get<Partial<MessageCounts>>('/sys/index/message/counts')
  const unread = Number(data?.unreadCount)
  const read = Number(data?.readCount)
  return {
    unreadCount: Number.isFinite(unread) && unread > 0 ? Math.floor(unread) : 0,
    readCount: Number.isFinite(read) && read > 0 ? Math.floor(read) : 0,
  }
}
